import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Breadcrumbs,
  Grid,
  Divider,
  Button,
  Skeleton,
  Chip,
  useTheme,
} from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getServicesIaCustomerSuscriptionByIdStore } from 'store/services-ia-customer-suscriptions/thunks';
import { servicesIaCustomerSuscriptionsMapper } from './mappers/suscripciones.mapper';
import {
  EditOutlined as EditIcon,
  CreditCardOutlined as CreditCardIcon,
  FormatListBulleted as ListIcon,
} from '@mui/icons-material';

export const DetailSuscripciones = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();

  const [item, setItem] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      const response = await dispatch(getServicesIaCustomerSuscriptionByIdStore(id));
      if (response?.ok && response?.data) {
        setItem(response.data);
      } else {
        // navigate('/manager/servicios-ia/comercial/suscripciones');
      }
    };

    loadData();
  }, [id, dispatch, navigate]);

  if (!item)
    return (
      <Box sx={{ p: 4 }}>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );

  const mappedDomain = servicesIaCustomerSuscriptionsMapper.toDomain(item);

  const formatDate = (date: any) => {
    if (!date) return 'No definido';
    return new Date(date).toLocaleDateString('es-ES');
  };

  return (
    <Box sx={{ width: '100%', mb: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
            Detalle de la Suscripción
          </Typography>
          <Breadcrumbs aria-label="breadcrumb">
            <Link color="inherit" to="/manager/plataforma-ia/comercial/suscripciones">
              Suscripciones
            </Link>
            <Typography color="text.primary">{mappedDomain.planName || 'Suscripción'}</Typography>
          </Breadcrumbs>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  color: theme.palette.primary.main,
                }}
              >
                <CreditCardIcon sx={{ fontSize: 40 }} />
              </Box>

              <Typography variant="h5" fontWeight={700} mb={1}>
                Plan: {mappedDomain.planName}
              </Typography>

              {mappedDomain.customer && mappedDomain.customer.label && (
                <Typography variant="subtitle2" color="text.secondary" mb={2}>
                  Cliente: {mappedDomain.customer.label}
                </Typography>
              )}

              <Chip
                label={
                  mappedDomain.statusSuscription === 'ACTIVE'
                    ? 'Activa'
                    : mappedDomain.statusSuscription
                }
                color={mappedDomain.statusSuscription === 'ACTIVE' ? 'success' : 'error'}
                size="small"
                sx={{ mb: 2 }}
              />

              <Divider sx={{ width: '100%', my: 3 }} />

              <Typography variant="h4" fontWeight={800} color="primary.main">
                {mappedDomain.monthlyCredits || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Créditos Mensuales
              </Typography>
              <Typography variant="h6" fontWeight={600} color="text.secondary" sx={{ mt: 1 }}>
                {mappedDomain.remainingCredits || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Créditos Restantes
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Card
            sx={{
              p: 4,
              borderRadius: 3,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
              height: '100%',
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              mb={3}
              display="flex"
              alignItems="center"
              gap={1}
            >
              <ListIcon sx={{ color: theme.palette.primary.main }} />
              Fechas y Renovación
            </Typography>

            <Grid container spacing={3} mb={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Fecha de Inicio
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {formatDate(mappedDomain.startsAt)}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Fecha de Expiración
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {formatDate(mappedDomain.expiresAt)}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Último Reinicio
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {formatDate(mappedDomain.lastResetAt)}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Tipo de Renovación
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {mappedDomain.renewalType || 'No definido'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card sx={{ p: 4, borderRadius: 3, boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)' }}>
            <Typography variant="h6" fontWeight={700} mb={3}>
              Detalles del Plan
            </Typography>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Precio Mensual
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {mappedDomain.price} {mappedDomain.currency}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Duración
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {mappedDomain.durationDays || 0} Días
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Peticiones / Día
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {mappedDomain.maxRequestsDay || 'Ilimitadas'}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Peticiones / Mes
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {mappedDomain.maxRequestsMonth || 'Ilimitadas'}
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Asistentes Máx.
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {mappedDomain.maxAssistants || 'Ilimitados'}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Agentes Máx.
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {mappedDomain.maxAgents || 'Ilimitados'}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Usuarios Máx.
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {mappedDomain.maxUsers || 'Ilimitados'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" fontWeight={700} mb={3}>
              Capacidades Incluidas
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {mappedDomain.allowChat && <Chip label="Chat" color="primary" variant="outlined" />}
              {mappedDomain.allowImages && (
                <Chip label="Imágenes" color="primary" variant="outlined" />
              )}
              {mappedDomain.allowAudio && <Chip label="Audio" color="primary" variant="outlined" />}
              {mappedDomain.allowDocuments && (
                <Chip label="Documentos" color="primary" variant="outlined" />
              )}
              {mappedDomain.allowEmbeddings && (
                <Chip label="Embeddings" color="primary" variant="outlined" />
              )}
              {mappedDomain.allowMemory && (
                <Chip label="Memoria" color="primary" variant="outlined" />
              )}
              {mappedDomain.allowTools && (
                <Chip label="Herramientas" color="primary" variant="outlined" />
              )}
              {mappedDomain.allowRag && <Chip label="RAG" color="primary" variant="outlined" />}

              {!mappedDomain.allowChat &&
                !mappedDomain.allowImages &&
                !mappedDomain.allowAudio &&
                !mappedDomain.allowDocuments &&
                !mappedDomain.allowEmbeddings &&
                !mappedDomain.allowMemory &&
                !mappedDomain.allowTools &&
                !mappedDomain.allowRag && (
                  <Typography variant="body2" color="text.secondary">
                    Ninguna capacidad especial habilitada.
                  </Typography>
                )}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
